// SurveyTable.jsx
import React, { useState, useContext, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Radio from '@mui/material/Radio';
import "./SurveyTable.css";
import viewEye from "assets/viewEye.svg"
import { useNavigate } from "react-router-dom";
import ConflictModal from "./ConflictModal";
import { set } from "date-fns";
import { formatSurveyDate } from "utils/utils";

// Add this function at the top of your component



// Add this helper function at the top of your component
const formatFarmerNames = (allFarmerDetails) => {
    if (!allFarmerDetails || !Array.isArray(allFarmerDetails)) return '';
    return allFarmerDetails.map(farmer => farmer.farmerName).join(', ');
};

// Add this helper function at the top of your component
const formatGrowerNumbers = (allGrowerDetails) => {
    if (!allGrowerDetails || !Array.isArray(allGrowerDetails)) return '';
    return allGrowerDetails.map(grower => grower.growerNumber).join(', ');
};

// Add this prop to receive selected status
const SurveyTable = ({ 
    isGlobalProfile, 
    isApprover, 
    data, 
    onAssignmentUpdate, 
    cropYear, 
    selectedStatusPickList, 
    selectedForReopen, 
    selectedForApprove,
    isVisitCompliance, // Add this prop
    selectedCountry,
    onViewSurvey,
    onBulkActionChange  // Add this new prop
}) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    // Add state for bulk selection
    const [bulkReopen, setBulkReopen] = useState(false);
    const [bulkApprove, setBulkApprove] = useState(false);
    const [conflictModalOpen, setConflictModalOpen] = useState(false);
    const [conflictMessage, setConflictMessage] = useState('');
    // Add function to check if bulk actions should be enabled
    const isBulkActionsEnabled = () => {
        // If only one status is selected and it's "Completed"
        return selectedStatusPickList?.length === 1 &&
            selectedStatusPickList[0]?.pickListDescription === "Completed";
    };
    useEffect(() => {
        // Reset bulk selection when data changes 
       
       
    }, [bulkReopen ,bulkApprove]);
    // Update the bulk selection handlers
    const handleBulkReopenChange = () => {
        if(!isBulkActionsEnabled()) {
            setConflictMessage(`Please select the completed status in the filter first before selecting Bulk select`)
            setConflictModalOpen(true);
            return
        }; // Only allow if "Completed" is selected
        const newState = !bulkReopen;
        if (newState == true && selectedForApprove?.size > 0) {
            setConflictMessage(`You cannot select both 'Approve' and 'Reopen' for the same farmer and crop stage. Values selected for 'Reopen' will remain selected during bulk 'Approve' selection. Please manually reopen the surveys selected as 'Reopen' or manually unselect the 'Reopen' surveys for later select bulk 'Approve'.`);
            setConflictModalOpen(true);
            return;
        }
        setBulkReopen(newState);
        setBulkApprove(false);
        // Notify parent component about bulk action change
        onBulkActionChange?.({ bulkReopen: newState, bulkApprove: false });

        // When deselecting, update all rows that were previously selected
        data?.forEach(item => {
            if (item.status === 'Completed') {
                const updatedItem = {
                    ...item,
                    isReopen: false,  // Always set to false when deselecting
                    isApproved: false
                };
                if (newState) {
                    updatedItem.isReopen = true;  // Only set to true if selecting
                }

                onAssignmentUpdate(updatedItem, 'bulk');
            }
        });
    };

    const handleBulkApproveChange = () => {
        if(!isBulkActionsEnabled()) {
            setConflictMessage(`Please select the completed status in the filter first before selecting Bulk select`)
            setConflictModalOpen(true);
            return
        }; // Only allow if "Completed" is selected
        const newState = !bulkApprove;
        if (newState == true && selectedForReopen?.size > 0) {
            setConflictMessage(`You cannot select both 'Reopen' and 'Approve' for the same farmer and crop stage. Values selected for 'Approve' will remain selected during bulk 'Reopen' selection. Please manually approve the surveys selected as 'Approve' or manually unselect the 'Approve' surveys for later select bulk 'Reopen'.`);
            setConflictModalOpen(true);
            return;
        }
        setBulkApprove(newState);
        setBulkReopen(false);
        // Notify parent component about bulk action change
        onBulkActionChange?.({ bulkReopen: false, bulkApprove: newState });

        // When deselecting, update all rows that were previously selected
        data?.forEach(item => {
            if (item.status === 'Completed') {
                const updatedItem = {
                    ...item,
                    isApproved: false,  // Always set to false when deselecting
                    isReopen: false
                };
                if (newState) {
                    updatedItem.isApproved = true;  // Only set to true if selecting
                }
                onAssignmentUpdate(updatedItem, 'bulk');
            }
        });
    };

    const handleView = (item) => {
        onViewSurvey(item);
    };

    const handleReopenChange = (event, item) => {
        if (item.status !== 'Completed') return; // Early return if not Completed

        if (onAssignmentUpdate) {
            const updatedItem = {
                ...item,
                isReopen: event.target.checked,
                isApproved: false // Reset approve when reopening
            };
            onAssignmentUpdate(updatedItem, 'individual');
        }
    };

    const handleApproveChange = (event, item) => {
        if (item.status !== 'Completed') return; // Early return if not Completed

        if (onAssignmentUpdate) {
            const updatedItem = {
                ...item,
                isApproved: event.target.checked,
                isReopen: false // Reset reopen when approving
            };
            onAssignmentUpdate(updatedItem, 'individual');
        }
    };

    // Update the radio button disable conditions
    const isRadioDisabled = (item) => {
        return item.status !== 'Completed'
         || 
               !isApprover || 
               isGlobalProfile || 
               isVisitCompliance;
    };

    const getStatusStyle = (status) => ({
        color: status?.toLowerCase() === 'completed' ? '#004F9F' : 'inherit'
    });

    return (
        <div className="table-container" style={{paddingTop:'20px'}}>
            <table className="custom-table">
                <thead>
                    <tr>
                        <th>
                            <span>{t('surveyTable.propertyNo')}</span>
                        </th>
                        <th>
                            <span>{t('surveyTable.growerNo')}</span>
                        </th>
                        {!isGlobalProfile && (
                            <th>
                                <span>{t('surveyTable.farmerName')}</span>
                            </th>
                        )}
                        <th>
                            <span>{t('surveyTable.surveyDate')}</span>
                        </th>
                        <th>
                            <span>{t('surveyTable.status')}</span>
                        </th>
                        <th>
                            <span>{t('surveyTable.approverName')}</span>
                        </th>
                        <th>
                            <div style={{ display: 'flex', justifyContent: 'flex-start', width: '100%', alignItems: 'center' }}>
                                <Radio
                                    checked={bulkReopen}
                                    onClick={handleBulkReopenChange}
                                    disabled={data?.length === 0 || !isApprover || isGlobalProfile || isVisitCompliance } // Disable if no data
                                    sx={{
                                        '& .MuiSvgIcon-root': {
                                            padding: 0,
                                            margin: 0,
                                        },
                                    }}
                                />
                                <span>{t('surveyTable.reopen')}</span>
                            </div>
                        </th>
                        <th>
                            <div style={{ display: 'flex', justifyContent: 'flex-start', width: '100%', alignItems: 'center' }}>
                                <Radio
                                    checked={bulkApprove}
                                    onClick={handleBulkApproveChange}
                                    disabled={data?.length === 0 || !isApprover || isGlobalProfile || isVisitCompliance } // Disable if no data
                                    sx={{
                                        '& .MuiSvgIcon-root': {
                                            padding: 0,
                                            margin: 0,
                                        },
                                    }}
                                />
                                <span>{t('surveyTable.approve')}</span>
                            </div>
                        </th>
                        <th>
                            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                <span>{t('surveyTable.actions')}</span>
                            </div>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {data?.map((item, index) => (
                        <tr key={item?.propertyFarmNumber || index}>
                            <td>{item?.propertyFarmNo}</td>
                            <td>{formatGrowerNumbers(item?.allFarmerDetails)}</td>
                            {!isGlobalProfile && (
                                <td>{formatFarmerNames(item?.allFarmerDetails)}</td>
                            )}
                            <td>{formatSurveyDate(item?.surveyDate)}</td>
                           
                            <td>
                                <span 
                                    style={{color: item?.status?.toLowerCase() === 'completed' ? '#004F9F' : 'inherit'}}
                                >
                                    {item?.status}
                                </span>
                            </td>
                            <td>{item?.approverName}</td>
                            <td style={{ justifyContent: 'center' }}>
                                <Radio
                                    checked={item?.status?.toLowerCase() == 're-opened' || Boolean(item?.isReopen)}
                                    onClick={(e) => handleReopenChange(e, item)}
                                    disabled={isRadioDisabled(item) || bulkApprove} // Only enable for Completed status
                                    sx={{
                                        '& .MuiSvgIcon-root': {
                                            padding: 0,
                                            margin: 0,
                                        },
                                    }}
                                />
                            </td>
                            <td style={{ justifyContent: 'center' }}>
                                <Radio
                                    checked={item?.status?.toLowerCase() == 'approved' || Boolean(item?.isApproved)}
                                    onClick={(e) => handleApproveChange(e, item)}
                                    disabled={isRadioDisabled(item) || bulkReopen} // Only enable for Completed status
                                    sx={{
                                        '& .MuiSvgIcon-root': {
                                            padding: 0,
                                            margin: 0,
                                        },
                                    }}
                                />
                            </td>
                            <td style={{ justifyContent: 'center' }}>
                                <IconButton
                                    size="small"
                                    onClick={() => handleView(item)}
                                    title="View Survey"
                                >
                                    <img src={viewEye} alt="View" style={{ width: 20, height: 20 }} />
                                </IconButton>
                            </td>
                        </tr>
                    ))}
                    {data?.length === 0 && (
                        <tr>
                            <td colSpan={9} style={{ textAlign: 'center' }}>
                                No data available
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
            {loading && (
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.7)'
                }}>
                    <CircularProgress />
                </div>
            )}
            <ConflictModal
                open={conflictModalOpen}
                onClose={() => setConflictModalOpen(false)}
                message={conflictMessage}
            />
        </div>
    );
};

export default SurveyTable;
